import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Crown, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const CATEGORY_LABELS = {
  youtube: { name: 'YouTube video', limitMsg: 'YouTube videos' },
  reel:    { name: 'Instagram Reel', limitMsg: 'Reels' },
  script:  { name: 'script',         limitMsg: 'scripts' },
  audio:   { name: 'audio file',     limitMsg: 'audio files' },
  image:   { name: 'image',          limitMsg: 'images' },
};

/**
 * Reusable Upgrade-to-Pro paywall modal.
 *
 * Props:
 *  - open (boolean) — controlled visibility
 *  - onOpenChange (fn) — controlled setter
 *  - category — 'youtube' | 'reel' | 'script' | 'audio' | 'image'
 *  - limit (number) — the free-tier cap for that category
 */
export default function PaywallModal({ open, onOpenChange, category, limit }) {
  const navigate = useNavigate();
  const labels = CATEGORY_LABELS[category] || { name: 'this content', limitMsg: 'generations' };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-zinc-950 border-zinc-800" data-testid="paywall-modal">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-400" />
            Upgrade to Pro
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          <div className="p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-200">
                  {labels.name.charAt(0).toUpperCase() + labels.name.slice(1)} limit reached
                </p>
                <p className="text-xs text-amber-300/80 mt-1">
                  Free accounts can generate only {limit} {labels.limitMsg}. Upgrade to Pro for unlimited {labels.limitMsg}.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-zinc-200">Pro plan includes</p>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                Unlimited scripts, images, audio, videos &amp; Reels
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                Priority video processing queue
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                Direct social media uploads (YouTube, Instagram, TikTok)
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                All AI generators with no daily caps
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={() => { onOpenChange(false); navigate('/subscription'); }}
              className="w-full h-11 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-zinc-950 font-semibold"
              data-testid="paywall-upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Pro
            </Button>
            <Button
              onClick={() => onOpenChange(false)}
              variant="ghost"
              className="w-full h-10 text-zinc-400 hover:text-zinc-200"
              data-testid="paywall-close-btn"
            >
              Maybe later
            </Button>
            <p className="text-[11px] text-zinc-500 text-center pt-1">
              Need help? Contact support to upgrade.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
